<template>
  <div class="styles-wrapper">
    <div v-for="(style, i) in styles">
      <div class="style-header">
        <span v-tooltip="getSkillDescription(style.id)" class="title">{{ $t(`skills.${style.id}.name`) }}</span>
        <div class="separator"></div>
      </div>
      <div v-if="style.id === 'leadership' && props.detailedPourcentageView" class="leadership-repartition-styles">
        <div class="style-names">
          <div class="name" v-for="(wording, index) in (style?.style_types || [])">
            {{ $t(`skills.${style.id}.game.styles.${wording.id}.title`) }}
          </div>
        </div>
        <div class="style-info-group">
          <div class="infos" v-for="wording in (style?.style_types || [])">
            <div v-if="wording.id !== 'unknown'" :style="{'--style-percent': ((props?.talent?.results?.scores?.[style.id]?.[wording.id] ?? 0) * 100) + '%'}" class="percent-bar-wrapper">
              <div :class="{'majority-style': isLeadingStyle(style.id, wording.id)}" class="bar"></div>
            </div>
            <div v-if="wording.id !== 'unknown'" class="percent-text">{{ (Math.round(props?.talent?.results?.scores?.[style.id]?.[wording.id] ?? 0) * 100) }}%</div>
            <div v-if="isLeadingStyle(style.id, wording.id)" class="majority-style-tag">{{ $t('app.campaign.result.panel.styles.majorityStyleTag') }}</div>
          </div>
        </div>
      </div>
      <div v-else class="styles">
        <div
          v-for="(wording, index) in (style?.style_types || [])"
          class="style-tag"
          :class="{selected: isLeadingStyle(style.id, wording.id)}"
          v-tooltip="$t(`skills.${style.id}.game.styles.${wording.id}.analysis.organization`)"
        >
          {{ $t(`skills.${style.id}.game.styles.${wording.id}.title`) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.styles-wrapper {
  flex-grow: 1;
  flex-basis: 0;
  
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-right: 10px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    padding-left: 4px;
    width: 3px;
    border-radius: 24px;
    background-color: var(--light-grey-color);
  }

  &::-webkit-scrollbar-thumb {
    background: $primary-color;
    padding: 4px;
    width: 3px;
    border-radius: 24px;
  }
}

.style-header {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;

  .title {
    flex-grow: 0;
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 12px;
    font-weight: 600;
  }

  .separator {
    flex-grow: 1;
    height: 0.5px;
    background: var(--secondary-color);
  }
}

.leadership-repartition-styles {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  .style-names {
    display: flex;
    flex-direction: column;
    gap: 9px;

    .name {
      display: flex;
      align-items: center;
      height: 27px;
      font-size: 12px;
    }
  }

  .style-info-group {
    display: flex;
    flex-direction: column;
    gap: 9px;

    .infos {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      height: 27px;

      .percent-bar-wrapper {
        width: 120px;
        height: 20px;
  
        .bar {
          background-color: var(--light-grey-color);
          border-radius: 4px;
          width: var(--style-percent);
          height: 20px;
  
          &.majority-style {
            background-color: $primary-color;
          }
        }
      }

      .percent-text {
        color: var(--secondary-color);
        text-align: center;
        font-family: $bioSans;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
      }

      .majority-style-tag {
        background-color: $primary-color;
        border-radius: 4px;
        padding: 6px;
        
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 11px;
        font-style: normal;
        font-weight: 600;
      }
    }
  }
}

.styles {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 5px 7px;
  gap: 6px;

  .style-tag {
    cursor: pointer;
    padding: 5px 7px;
    border-radius: 2px;
    user-select: none;

    color: var(--grey-50-color);
    font-family: $bioSans;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 100%; /* 14px */
    background-color: var(--light-grey-color);

    &.selected {
      color: $black-color;
      background-color: $primary-color;
    }
  }

  .leadership-repartition {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    .style-names {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .style-infos {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .percent-bar-wrapper {
        width: 120px;
  
        .bar {
          background-color: var(--light-grey-color);
          border-radius: 4px;
          width: var(--style-percent);
  
          &.majority-style {
            background-color: $primary-color;
          }
        }
      }
  
      .majority-style-tag {
        background-color: $primary-color;
        border-radius: 4px;
        padding: 6px;
        
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
      }
    }

  }
} 
</style>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { useCampaign } from '@/queries/campaigns.query';
import { ModuleLabel } from '@/types/campaign';

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();
const appStore = useAppStore();

const { data: campaign, isPending } = useCampaign();

const skillsStore = useSkillsStore();

const props = defineProps<{
  talent: any,
  detailedPourcentageView: boolean,
}>();

const styles = computed(() => {
  if (isPending.value) return [];
  return skillsStore.getCampaignStyles(campaign.value);
});

function isLeadingStyle(skillName: string, style: string) {
  if (!props?.talent?.results?.styles) return false;
  return props.talent.results.styles?.[skillName] === style;
}

function getSkillDescription(skillId: string) {
  const tRes = t(`skills.${skillId}.app.description`);
  return (tRes !== `skills.${skillId}.app.description` ? tRes : null);
}

</script>